<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const contactPanelTitle = 'Dados do contato';
// Aceleres: no desktop o painel de contato deixou de flutuar por cima da
// conversa -- ele fica ancorado como irmao de flex da ConversationBox (ver
// ConversationView.vue) e espreme a conversa, com esta largura fixa.
const CONTACT_PANEL_WIDTH = 500;
const isClosing = ref(false);
const isCompactPanelLayout = ref(false);
let closeTimer;

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    return 0;
  }
  return null;
});

// No modo ancorado a largura vem do CSS (`--contact-panel-width`); o
// container so precisa preencher o proprio painel. No compacto (celular /
// touch) ele continua ocupando a tela inteira.
const modalOverlayStyle = computed(() => ({
  '--contact-panel-width': `${CONTACT_PANEL_WIDTH}px`,
}));

const modalContainerStyle = computed(() => ({ width: '100%' }));

const updatePanelLayout = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const isNarrowViewport =
    window.matchMedia?.('(max-width: 767px)').matches ?? false;
  const isTouchViewport =
    window.matchMedia?.('(pointer: coarse) and (max-width: 1024px)').matches ??
    false;
  isCompactPanelLayout.value = isNarrowViewport || isTouchViewport;
};

const closeContactPanel = () => {
  if (
    !uiSettings.value?.is_contact_sidebar_open &&
    !uiSettings.value?.is_copilot_panel_open
  ) {
    return;
  }

  isClosing.value = true;
  window.clearTimeout(closeTimer);
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

// Aceleres -- Esc fecha o painel de dados do contato, como fecha qualquer
// painel sobreposto. Duas guardas: se a tecla ja foi tratada por quem esta por
// cima (`defaultPrevented`) ou se ha um modal aberto — o de midias, o de
// encaminhar, os de confirmacao —, o Esc pertence a ele. Sem isso, um Esc
// fechava os dois de uma vez e o painel sumia debaixo do usuario.
// `alertdialog` e `aria-modal` entram junto de `dialog`: os quatro modais de
// confirmacao do painel (limpar, bloquear, sair do grupo, excluir) sao
// `role="alertdialog"` e ficariam de fora — o Esc fecharia o painel por baixo
// deles e o modal sobraria orfao em tela.
const OVERLAY_SELECTOR =
  '.modal-mask, [role="dialog"], [role="alertdialog"], [aria-modal="true"], .contact-details-media-modal';

// Vale so o overlay que esta MESMO em tela: varios componentes deixam o
// proprio no no DOM enquanto escondidos, e procurar so pelo seletor fazia o
// Esc achar que havia sempre algo aberto por cima — nunca fechava o painel.
//
// Aceleres 2026-08-17: e o proprio painel precisa ficar de fora da varredura.
// O container dele tambem e um `role="dialog"` (ver o template), entao ele
// casava com o seletor e se contava como "algo aberto por cima de mim": com o
// painel aberto o Esc nunca fechava nada. Contar so quem NAO e o painel
// preserva a intencao original — um modal nascido dentro dele (as midias, a
// edicao do contato) continua ficando com a tecla.
const panelContainer = ref(null);

const hasVisibleOverlayAbovePanel = () =>
  [...document.querySelectorAll(OVERLAY_SELECTOR)].some(
    element =>
      element !== panelContainer.value && element.getClientRects().length > 0
  );

const isPanelOpen = () =>
  Boolean(
    uiSettings.value?.is_contact_sidebar_open ||
      uiSettings.value?.is_copilot_panel_open
  );

const handleEscape = event => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  // Com o painel fechado a tecla nao e nossa: quem escuta depois usa o Esc
  // para sair da conversa (ConversationView).
  if (!isPanelOpen()) return;
  if (hasVisibleOverlayAbovePanel()) return;

  // Um Esc fecha UMA coisa. Sem consumir o evento aqui, o mesmo Esc ainda
  // poderia sair da conversa junto com o painel.
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();

  closeContactPanel();
};

const handleContactPanelOutsideClick = () => {
  // Ancorado o painel nao e mais um modal: ele divide a tela com a conversa,
  // entao clicar na conversa (numa mensagem, no editor) nao pode fecha-lo.
  // No compacto ele volta a cobrir a tela e o clique fora segue fechando.
  if (!isCompactPanelLayout.value) {
    return;
  }

  closeContactPanel();
};

watch(activeTab, async value => {
  if (value === 0) {
    isClosing.value = false;
    await nextTick();
    updatePanelLayout();
  }
});

onMounted(async () => {
  await nextTick();
  updatePanelLayout();

  window.addEventListener('resize', updatePanelLayout);
  // Na fase de captura: assim a tecla chega aqui antes de qualquer bolha que
  // pare a propagacao pelo caminho (editor, menus, o proprio painel).
  document.addEventListener('keydown', handleEscape, true);
});

onBeforeUnmount(() => {
  window.clearTimeout(closeTimer);
  window.removeEventListener('resize', updatePanelLayout);
  document.removeEventListener('keydown', handleEscape, true);
});
</script>

<template>
  <Teleport to="body" :disabled="!isCompactPanelLayout">
    <div
      class="contact-details-modal-overlay pointer-events-none flex min-w-0 justify-end overflow-x-hidden overflow-y-hidden"
      :class="{
        'contact-details-modal-overlay--compact fixed inset-0 !z-[2147483646]':
          isCompactPanelLayout,
        'contact-details-modal-overlay--docked relative': !isCompactPanelLayout,
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
      }"
      :style="modalOverlayStyle"
    >
      <div
        ref="panelContainer"
        v-on-click-outside="[
          handleContactPanelOutsideClick,
          {
            // A galeria e o modal de reenvio nascem fora do painel (teleport),
            // e fechar o painel desmonta a ConversationSidebar via v-if --
            // levando a galeria junto no meio do clique.
            ignore: [
              '.contact-delete-modal--block',
              '.contact-edit-drawer',
              '.contact-details-media-modal',
              '.gallery-viewer-overlay',
              '.contact-mobile-avatar-viewer',
              '.acl-contact-pdf-viewer',
              '.acl-forward-overlay',
            ],
          },
        ]"
        class="contact-details-modal-container pointer-events-auto relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l border-n-weak bg-white shadow-[0_24px_70px_rgba(15,23,42,0.24)]"
        :class="{
          'contact-details-modal-container--closing': isClosing,
          'contact-details-modal-container--compact': isCompactPanelLayout,
        }"
        :style="modalContainerStyle"
        role="dialog"
        :aria-modal="isCompactPanelLayout ? 'true' : 'false'"
        :aria-label="contactPanelTitle"
      >
        <ContactPanel
          v-show="activeTab === 0"
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
// Aceleres: ancorado -- o painel e um irmao de flex da ConversationBox
// (ConversationView.vue), entao a conversa (flex-1 min-w-0) encolhe em vez de
// ficar coberta. O z-index alto so faz sentido no compacto, onde ele volta a
// ser overlay sobre a tela toda.
//
// 2026-09-01: os dois passaram a dividir em partes iguais o espaco que sobra
// depois da lista de conversas. `flex: 1 1 0` e o mesmo `flex-1` da
// ConversationBox -- mesma base (0) e mesmo crescimento dao a mesma largura.
// Antes o painel era `0 0 500px` e so a conversa pagava a conta.
// A lista nao entra na divisao: ela e `flex-shrink-0` com largura propria
// (ChatList.vue), entao mantem a medida que tiver quando o painel abrir.
// `--contact-panel-width` continua definido no componente, mas so o compacto
// depende dele.
.contact-details-modal-overlay--docked {
  flex: 1 1 0;
  width: auto;
  max-width: 100%;
  height: 100%;
  min-height: 0;
}

.contact-details-modal-overlay--compact {
  inset: 0 !important;
  width: auto !important;
  max-width: none;
}

/* Aceleres: o painel telemovel e teletransportado para o body e, por isso,
   nao herda o recuo de area segura dos containers da conversa. Reservar os
   quatro lados aqui mantem a ficha e os botoes dentro da area clicavel tambem
   quando o aparelho gira e o recorte passa para as laterais. */
@media (max-width: 767px) {
  .contact-details-modal-overlay--compact {
    box-sizing: border-box;
    top: max(
      var(--aceleres-app-safe-top, 0px),
      env(safe-area-inset-top, 0px)
    ) !important;
    right: max(
      var(--aceleres-app-safe-right, 0px),
      env(safe-area-inset-right, 0px)
    ) !important;
    bottom: max(
      var(--aceleres-app-safe-bottom, 0px),
      env(safe-area-inset-bottom, 0px)
    ) !important;
    left: max(
      var(--aceleres-app-safe-left, 0px),
      env(safe-area-inset-left, 0px)
    ) !important;
  }
}

.contact-details-modal-container {
  box-sizing: border-box;
  max-width: 100%;
  animation: contact-details-panel-enter 220ms ease-out;
  border-radius: 0;
  transition: transform 220ms ease-in-out;
}

.contact-details-modal-container--compact {
  width: 100% !important;
  height: 100% !important;
  min-height: 0;
  border-right: 0;
  border-left: 0;
  border-radius: 0;
}

.contact-details-modal-container--closing {
  transform: translateX(100%);
}

@keyframes contact-details-panel-enter {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}
</style>
